import { Slider } from '@base-ui/react';
import type { Theme } from '@emotion/react';
import styled from '@emotion/styled';
import { alpha } from '@mui/material/styles';

import type { SliderColorPalette, SliderSize } from './types';

const TRACK_HEIGHT: Record<SliderSize, number> = { sm: 6, md: 8, lg: 12 };

const THUMB_SIZE = 16;

const noDollarProp = (prop: string) => !prop.startsWith('$');

const getPaletteColors = (theme: Theme, colorPalette: SliderColorPalette) =>
  colorPalette === 'info' ? theme.colors.info : theme.colors.brand;

export const StyledContainer = styled('div', { shouldForwardProp: noDollarProp })<{
  $orientation: 'horizontal' | 'vertical';
}>(({ $orientation }) => ({
  display: 'flex',
  flexDirection: $orientation === 'vertical' ? 'row' : 'column',
  alignItems: $orientation === 'vertical' ? 'flex-start' : undefined,
  gap: 8,
  width: $orientation === 'vertical' ? 'auto' : '100%',
  height: $orientation === 'vertical' ? '100%' : 'auto',
}));

export const StyledRow = styled('div', { shouldForwardProp: noDollarProp })<{
  $orientation: 'horizontal' | 'vertical';
}>(({ $orientation }) => ({
  display: 'flex',
  flexDirection: $orientation === 'vertical' ? 'column' : 'row',
  alignItems: 'center',
  gap: 12,
  width: $orientation === 'vertical' ? 'auto' : '100%',
  height: $orientation === 'vertical' ? '100%' : 'auto',
}));

export const StyledRoot = styled(Slider.Root)({
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  padding: 0,
  cursor: 'pointer',
  touchAction: 'none',
  userSelect: 'none',
  WebkitTapHighlightColor: 'transparent',

  '&[data-orientation="vertical"]': {
    display: 'inline-flex',
    flexDirection: 'column',
    width: 'auto',
    height: '100%',
  },

  '&[data-disabled]': {
    cursor: 'not-allowed',
    pointerEvents: 'none',
    opacity: 0.4,
  },
});

export const StyledControl = styled(Slider.Control)({
  display: 'flex',
  alignItems: 'center',
  flex: 1,
  width: '100%',
  height: '100%',

  '[data-orientation="vertical"] &': {
    flexDirection: 'column',
    width: 'auto',
    height: '100%',
  },
});

export const StyledTrack = styled(Slider.Track, { shouldForwardProp: noDollarProp })<{
  $size: SliderSize;
}>(({ theme, $size }) => ({
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  borderRadius: theme.radius.full,
  backgroundColor: theme.colors.background.overlay,

  '[data-orientation="horizontal"] &': {
    width: '100%',
    height: TRACK_HEIGHT[$size],
  },

  '[data-orientation="vertical"] &': {
    width: TRACK_HEIGHT[$size],
    height: '100%',
    flexDirection: 'column',
  },
}));

export const StyledIndicator = styled(Slider.Indicator, { shouldForwardProp: noDollarProp })<{
  $colorPalette: SliderColorPalette;
}>(({ theme, $colorPalette }) => ({
  borderRadius: theme.radius.full,
  backgroundColor: getPaletteColors(theme, $colorPalette).solid,

  '[data-orientation="horizontal"] &': {
    height: '100%',
  },

  '[data-orientation="vertical"] &': {
    width: '100%',
  },
}));

export const StyledThumb = styled(Slider.Thumb, { shouldForwardProp: noDollarProp })<{
  $colorPalette: SliderColorPalette;
}>(({ theme, $colorPalette }) => ({
  width: THUMB_SIZE,
  height: THUMB_SIZE,
  borderRadius: theme.radius.full,
  backgroundColor: getPaletteColors(theme, $colorPalette).hover,
  border: `2px solid ${theme.colors.text.primary}`,
  outline: 'none',
  cursor: 'pointer',
  boxSizing: 'border-box',
  flexShrink: 0,
  boxShadow: '0px 1px 0px 0px rgba(142, 142, 142, 0.1)',
  transition: 'box-shadow 0.15s ease',

  '&:hover': {
    boxShadow: `0px 0px 0px 3px ${alpha(theme.colors.text.primary, 0.2)}`,
  },

  '&:focus-visible': {
    boxShadow: `0px 0px 0px 3px ${alpha(theme.colors.text.primary, 0.2)}`,
  },

  '&[data-disabled]': {
    cursor: 'not-allowed',
  },
}));

export const StyledMarksRow = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
});

export const StyledTicksRow = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
  height: 12,
});

export const StyledTick = styled('div')(({ theme }) => ({
  width: 1,
  height: 8,
  backgroundColor: theme.colors.border.default,
}));
